"use client"

import { Button } from "@/components/ui/button"
import { useRef, useState } from "react"
import CropImageModal from "./CropImageModal"
import { uploadImage } from "@/lib/api/notes"
import { Trash2 } from "lucide-react"
import { ImageWithPlaceholder } from "@types"
import { toast } from "sonner"
import clsx from "clsx"

export const ImagePicker = ({
  imgSrc,
  handleSave,
  handleDelete,
  actionText,
  showImageShadow,
  wrapperClassName,
  buttonClassName,
  imageClassName,
  showDefaultDeleteButton = true,
  aspect = 2,
  customActionButtons,
}: {
  imgSrc?: string
  handleSave(img: ImageWithPlaceholder): Promise<void>
  handleDelete(): Promise<void>
  actionText?: string
  showImageShadow?: boolean
  wrapperClassName?: string
  buttonClassName?: string
  imageClassName?: string
  showDefaultDeleteButton?: boolean
  aspect?: number
  customActionButtons?: React.ReactNode
}) => {
  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File>()
  const [imageSource, setImageSource] = useState(imgSrc)
  const [isCropModalOpen, setIsCropModalOpen] = useState(false)

  function dragOverHandler(ev: any) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault()
  }

  const dropHandler = (ev: any) => {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault()

    let file: File | undefined
    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      const item = ev.dataTransfer.items[0]
      if (item.kind === "file") file = item.getAsFile()
    } else {
      // Use DataTransfer interface to access the file(s)
      file = ev.dataTransfer.files[0]
    }

    if (!file || !file.type.includes("image")) return null
    setFile(file)
    const reader = new FileReader()
    reader.addEventListener("load", () => {
      setImageSource(reader.result?.toString() || "")
      setIsCropModalOpen(true)
    })
    reader.readAsDataURL(file)
  }

  const triggerFileInput = () => {
    fileInput.current?.click()
  }

  return (
    <div>
      <div className="group">
        <div
          className={clsx(
            `relative flex justify-center rounded-md cursor-pointer text-muted-foreground focus-within:border-black focus-within:text-black hover:border/50 hover:text-muted-foreground/80 transition-all`,
            wrapperClassName
          )}
          onClick={triggerFileInput}
          onDrop={dropHandler}
          onDragOver={dragOverHandler}
        >
          <div>
            {imgSrc ? (
              <img
                className={clsx(
                  `object-cover object-center`,
                  showImageShadow && "shadow-2xl",
                  imageClassName
                )}
                src={imgSrc}
                alt="Uploaded image"
              />
            ) : (
              <div className="px-6 pt-5 pb-6 space-y-1 text-center border">
                <svg
                  className="mx-auto h-12 w-12 dark-text-description"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p>
                  {actionText
                    ? actionText
                    : "Drag image to upload or click here"}
                </p>
              </div>
            )}
            <input
              ref={fileInput}
              id="file-upload"
              name="file-upload"
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={async (ev) => {
                const file = ev.target.files?.[0]
                if (!file) return null
                setFile(file)
                // Clear input value so user can select the same image again & re-crop
                if (fileInput && fileInput.current) fileInput.current.value = ""
                const reader = new FileReader()
                reader.addEventListener("load", () => {
                  setImageSource(reader.result?.toString() || "")
                  setIsCropModalOpen(true)
                })
                reader.readAsDataURL(file)
              }}
            />
          </div>
        </div>
        {imgSrc && showDefaultDeleteButton && (
          <Button
            className={clsx(
              "invisible group-hover-always:visible absolute rounded-xl",
              buttonClassName
            )}
            size="icon"
            onClick={() => handleDelete()}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {customActionButtons && (
        <div className="absolute bottom-2 right-2">{customActionButtons}</div>
      )}

      {file && imageSource && (
        <CropImageModal
          open={isCropModalOpen}
          setOpen={setIsCropModalOpen}
          circularCrop={false}
          aspect={aspect}
          originalImage={file}
          imageSource={imageSource}
          handleSave={async (file) => {
            if (file) {
              const imgWithPlaceholder = await uploadImage(file)

              if (!imgWithPlaceholder) {
                console.error("Unable to upload")
                toast.error("Failed to upload image. Please try again.")
                return
              }

              handleSave(imgWithPlaceholder)
            }
          }}
        />
      )}
    </div>
  )
}
